import * as Sentry from "@sentry/react"
import {Replay} from "@sentry/replay"
import {isSentryReplayAllowed, parseStrictPositiveInt} from "./sentryReplayAllowlist"

const TENANT_TAG = "tenant"
const APP_TAG = "app"
const SERVICE_TAG = "service"
const ENV_TAG = "env"
const SENTRY_NODE_ENV_TAG = "sentry_node_env"

export function getSentryNodeEnv(): string {
    const raw =
        process.env.REACT_APP_SENTRY_NODE_ENV ||
        (process.env as Record<string, string | undefined>).SENTRY_NODE_ENV ||
        process.env.NODE_ENV
    if (typeof raw === "string" && raw.trim().length > 0) {
        return raw.trim().toLowerCase()
    }
    return "development"
}

export function initSentry(service: "platform" | "web" = "web"): void {
    const dsn = process.env.REACT_APP_SENTRY_DSN
    if (!dsn) {
        return
    }

    const tenant = (process.env.REACT_APP_TENANT_SLUG || "bellus").trim().toLowerCase()
    const app = `admissions-${tenant}-${service}`
    const nodeEnv = getSentryNodeEnv()

    Sentry.init({
        dsn,
        integrations: [
            new Replay({
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true
            })
        ],
        environment: nodeEnv,
        tracesSampleRate: 0,
        // Replay stays off for everyone until Model allowlist calls Replay.start().
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        initialScope: {
            tags: {
                app,
                service,
                tenant,
                [ENV_TAG]: nodeEnv,
                [SENTRY_NODE_ENV_TAG]: nodeEnv
            }
        }
    })
}

/** Identity that owns the currently running replay, if any (`tenant:userId`). */
let activeReplayIdentity: string | null = null

let handoffGeneration = 0

function getReplayIntegration(): Replay | null {
    const client = Sentry.getCurrentHub().getClient()
    if (!client) {
        return null
    }
    return client.getIntegration(Replay)
}

export function buildSentryReplayIdentityKey(
    tenantSlug: string | null | undefined,
    userId: number | string | null | undefined
): string | null {
    if (typeof tenantSlug !== "string") {
        return null
    }
    const tenant = tenantSlug.trim().toLowerCase()
    if (tenant.length === 0) {
        return null
    }
    const id = parseStrictPositiveInt(userId)
    if (id === null) {
        return null
    }
    return `${tenant}:${id}`
}

export function isReplayHandoffCurrent(
    scheduledGeneration: number,
    currentGeneration: number = handoffGeneration
): boolean {
    return scheduledGeneration === currentGeneration
}

function resetTenantTag(): void {
    const defaultSlug = (process.env.REACT_APP_TENANT_SLUG || "bellus").trim().toLowerCase()
    const nodeEnv = getSentryNodeEnv()
    Sentry.setTag(TENANT_TAG, defaultSlug)
    Sentry.setTag(APP_TAG, `admissions-${defaultSlug}-web`)
    Sentry.setTag(SERVICE_TAG, "web")
    Sentry.setTag(ENV_TAG, nodeEnv)
    Sentry.setTag(SENTRY_NODE_ENV_TAG, nodeEnv)
}

function startReplayForIdentity(replay: Replay, identityKey: string): void {
    try {
        replay.start()
        activeReplayIdentity = identityKey
    } catch {
        activeReplayIdentity = null
    }
}

export function getActiveReplayIdentity(): string | null {
    return activeReplayIdentity
}

export function enableSentryReplayIfAllowed(
    tenantSlug: string | null | undefined,
    userId: number | string | null | undefined
): void {
    if (!isSentryReplayAllowed({tenantSlug, userId})) {
        disableSentryReplay()
        return
    }

    const identityKey = buildSentryReplayIdentityKey(tenantSlug, userId)
    if (identityKey === null) {
        disableSentryReplay()
        return
    }

    const slug = (tenantSlug as string).trim().toLowerCase()
    const nodeEnv = getSentryNodeEnv()
    Sentry.setTag(TENANT_TAG, slug)
    Sentry.setTag(APP_TAG, `admissions-${slug}-web`)
    Sentry.setTag(SERVICE_TAG, "web")
    Sentry.setTag(ENV_TAG, nodeEnv)
    Sentry.setTag(SENTRY_NODE_ENV_TAG, nodeEnv)

    const replay = getReplayIntegration()
    if (!replay) {
        return
    }

    const alreadyRecording = Boolean(replay.getReplayId())
    if (alreadyRecording) {
        activeReplayIdentity = identityKey
        return
    }

    startReplayForIdentity(replay, identityKey)
}

export function disableSentryReplay(): void {
    handoffGeneration += 1
    resetTenantTag()
    activeReplayIdentity = null

    const replay = getReplayIntegration()
    if (!replay) {
        return
    }
    if (!replay.getReplayId()) {
        return
    }
    void replay.stop().catch(() => undefined)
}
